import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  InitiateCall,
  UpdateCall,
  QueueCall,
  DismissCallAlert,
  LoadUnendedCall,
  ClearActiveCall,
  ClearDispositionCall,
  MarkCallDispositioned,
  HoldCall,
  UnHoldCall,
  InitiateSecondCall,
  SwapCalls,
  MergeCalls,
  MuteState,
  TransferCall,
  CancelCall,
  ToggleSilentMonitorMuteState
} from 'app/store/call/call.actions';
import {
  ActiveCall,
  Call,
  CallMuteStatus,
  CallTimers,
  CallUpdates,
  CallWrapUpEvent,
} from 'app/models/dto/call.model';
import { CallState } from 'app/store/call/call.state';
import { Observable, Subscription, combineLatest, Subject } from 'rxjs';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { WrapUpDialogV2Component } from 'app/call-display/call-display/wrap-up-dialog-v2/wrap-up-dialog-v2.component';
import { GetUserQueue, DeleteBorrowerFromUserQueue } from 'app/store/queue/queue.actions';
import { QueueState } from 'app/store/queue/queue.state';
import { Queue } from 'app/models/queue/queue.model';
import { Borrower } from 'app/models/dto/borrower.model';
import { Alert } from 'app/models/dto/alert.model';
import { map, take, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'ng2-app/models/dto/lender-user.model';
import { CheckNewLeadCallTask } from 'ng2-app/modules/today/state/user-action-items.action';
import { LoadNeedsDisposition } from 'app/store/disposition/disposition.actions';
import { DispositionState } from 'app/store/disposition/disposition.state';
import { NeedsDisposition } from 'app/models/disposition/disposition.model';

const tabId = Math.random().toString(36).slice(2, 11);

interface WrapUpBroadcast {
  action: 'updateEndedCall' | 'confirmedEndCall' | 'forcedDispositionComplete';
  updates?: CallUpdates;
  callId?: number | null;
}

@Component({
  selector: 'app-call-display-smart',
  templateUrl: './call-display-smart.component.html',
  styleUrls: ['./call-display-smart.component.scss'],
  standalone: false
})
export class CallDisplaySmartComponent implements OnInit, OnDestroy {

  call$: Observable<ActiveCall> = this.store.select(CallState.call);
  secondaryCall$: Observable<ActiveCall> = this.store.select(CallState.secondaryCall);
  dispositionCall$: Observable<ActiveCall> = this.store.select(CallState.dispositionCall);
  callStateIsProcessing$: Observable<boolean> = this.store.select(CallState.processing);
  twilioDeviceIsLoading$: Observable<boolean> = this.store.select(CallState.twilioDeviceIsLoading);
  silentMonitorInProgress$: Observable<boolean> = this.store.select(CallState.silentMonitorInProgress);
  alerts$: Observable<Alert[]> = this.store.select(CallState.alerts);
  callTimers$: Observable<CallTimers> = this.store.select(CallState.callTimers);
  callMuteStatus$: Observable<CallMuteStatus> = this.store.select(CallState.callMuteStatus);
  queue$: Observable<Queue[]> = this.store.select(QueueState.queue);
  lenderUser$: Observable<LenderUser | null> = this.store.select(LenderUserState.lenderUser);
  needsDisposition$: Observable<NeedsDisposition> = this.store.select(DispositionState.needsDisposition);
  teamType$: Observable<string | null> = this.lenderUser$.pipe(map((u) => u?.team?.type ?? null));
  canUnmuteSilentMonitorCall$: Observable<boolean> = this.store.select(LenderUserState.hasPermission('canUnmuteSilentMonitorCall'));

  destroyed$ = new Subject<boolean>();
  activeCall: ActiveCall;
  secondaryCall: ActiveCall;
  dispositionCall: ActiveCall | null;
  callSub: Subscription;
  calledBorrower: Borrower;
  currentQueue: Queue[];
  queueSub: Subscription;
  sourceTabId = tabId;

  private wrapUpDialogRef?: MatDialogRef<WrapUpDialogV2Component>;
  unendedCall = false;
  timerOpen: boolean;
  channel: BroadcastChannel;

  private midCallCompletedForCallId: number | null = null;

  get midCallDispositionCompleted(): boolean {
    if (this.midCallCompletedForCallId === null) return false;
    const currentId = this.activeCall?.callId ?? this.dispositionCall?.callId;
    return currentId === undefined || currentId === this.midCallCompletedForCallId;
  }

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit() {
    this.channel = new BroadcastChannel('wrapup_channel');
    this.channel.onmessage = (event) => this.handleWrapUpBroadcast((event.data ?? {}) as WrapUpBroadcast);

    this.store.dispatch(new GetUserQueue());
    this.checkForUnendedCall();
    this.queueSub = this.queue$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(queue => this.currentQueue = queue);
    this.callSub = this.call$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(call => {
        this.handleActiveCallChange(call);
        this.activeCall = call;
        if (call && call.borrower) {
          this.activeCall = call;
          this.calledBorrower = call.borrower;
        }
      });
    this.dispositionCall$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: disCall => this.dispositionCall = disCall,
      error: (error) => console.error('Error in dispositionCall subscription:', error)
    });
    // Server-truth self-heal: re-pop the non-CS disposition modal whenever the
    // server reports an open call (fresh load, post-call, or backlog after a
    // save), so a missed disconnect event can no longer strand a call as NULL.
    this.needsDisposition$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: needsDisposition => this.maybeForceDisposition(needsDisposition),
      error: (error) => console.error('Error in needsDisposition subscription:', error)
    });
    this.secondaryCall$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: secondCall => this.secondaryCall = secondCall,
      error: (error) => console.error('Error in secondaryCall subscription:', error)
    });
    this.callStateIsProcessing$.pipe(takeUntil(this.destroyed$)).subscribe({
      error: (error) => console.error('Error in callStateIsProcessing subscription:', error)
    });
    this.silentMonitorInProgress$.pipe(takeUntil(this.destroyed$)).subscribe({
      error: (error) => console.error('Error in silentMonitorInProgress subscription:', error)
    });
    this.callTimers$.pipe(takeUntil(this.destroyed$)).subscribe({
      error: (error) => console.error('Error in callTimers subscription:', error)
    });
    this.callMuteStatus$.pipe(takeUntil(this.destroyed$)).subscribe({
      error: (error) => console.error('Error in callMuteStatus subscription:', error)
    });

    // collapse the callbar once the queue has been cleared and there is no active call
    combineLatest([this.call$, this.queue$]).pipe(takeUntil(this.destroyed$)).subscribe({
      next: ([call, queue]) => {
        if (call && !!call.callId !== true && !queue.length) {
          this.store.dispatch(new ClearActiveCall());
        }
      },
      error: (error) => {
        console.error('Error in call/queue combineLatest subscription:', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  checkForUnendedCall() {
    const unendedCall = JSON.parse(localStorage.getItem('currentCall') || '{}');
    if (unendedCall.callId && !/^(conference|internal|add) call/i.exec(<string> unendedCall.disposition)) {
      this.unendedCall = true;
      this.store.dispatch(new LoadUnendedCall(unendedCall.callId));
    }
  }

  initiateCall(call: Call): void {
    this.store.dispatch(new InitiateCall(call));
    this.router.navigate(['/borrower/', call.borrowerId]);
  }

  endCall(updates: CallUpdates): void {
    this.store.dispatch(new UpdateCall(updates));
  }

  updateCall(updates: CallUpdates): void {
    this.store.dispatch(new UpdateCall(updates));
  }

  destroyCall(callId): void {
    this.store.dispatch(new CancelCall);
    this.timerOpen = false;
    localStorage.removeItem('currentCall');
  }

  muteCall(setting): void {
    this.store.dispatch(new MuteState(setting));
  }

  toggleSilentMonitorMuteState(setting): void {
    this.store.dispatch(new ToggleSilentMonitorMuteState(setting));
  }

  placeCallOnHold(holdStatus: boolean): void {
    if (holdStatus && !!this.activeCall.callId) {
      this.store.dispatch(new HoldCall(this.activeCall.callId));
    } else if (!holdStatus && !!this.activeCall.callId) {
      this.store.dispatch(new UnHoldCall(this.activeCall.callId));
    }
  }

  transferCall(callInfo: object): void {
    this.store.dispatch(new TransferCall(callInfo));
  }

  startSecondCall(callInfo: object): void {
    this.store.dispatch(new InitiateSecondCall(callInfo));
  }

  swapCalls(): void {
    this.store.dispatch(new SwapCalls());
  }

  mergeCalls(): void {
    this.store.dispatch(new MergeCalls());
  }

  queueCall(call: Queue): void {
    this.store.dispatch(new QueueCall(call));
  }

  advanceQueue(): void {
    const nextInQueue = this.currentQueue?.[0];
    if (!nextInQueue) {
      return;
    }
    if (this.activeCall?.borrowerId === nextInQueue.borrowerId) {
      this.router.navigate(['/borrower/', nextInQueue.borrowerId]);
    } else {
      this.queueCall(nextInQueue);
    }
  }

  shouldQueueAdvance(): boolean {
    return this.currentQueue &&
      this.currentQueue.length > 0 &&
      (this.activeCall || this.dispositionCall) &&
      (this.activeCall?.callState === 'disconnected' || !this.activeCall?.inProgress);
  }

  wrapUpDialog(callWrapUpEvent: CallWrapUpEvent) {
    if (this.isWrapUpDialogOpen()) {
      return;
    }

    if (this.sourceTabId !== callWrapUpEvent.sourceTabId) {
      return;
    }

    if (this.shouldUseForcedCxInteractionDrawer()) {
      if (callWrapUpEvent.checkEndCall) {
        this.endCall({ end: true });
      }
      return;
    }

    const dispositionedThisCall = callWrapUpEvent.callId != null
      ? this.midCallCompletedForCallId === callWrapUpEvent.callId
      : this.midCallDispositionCompleted;

    if (dispositionedThisCall) {
      if (callWrapUpEvent.checkEndCall) {
        this.endCall({ end: true });
        this.channel.postMessage({ action: 'confirmedEndCall', updates: { end: true }, callId: this.activeCall?.callId ?? null });
      } else {
        Promise.resolve().then(() => this.completeCallWrapUp());
      }
      return;
    }

    const dialogRef = this.wrapUpDialogRef = this.dialog.open<WrapUpDialogV2Component>(WrapUpDialogV2Component, {
      disableClose: true,
      data: {
        activeCall: this.activeCall,
        checkEndCall: callWrapUpEvent.checkEndCall,
        borrowersInQueue: this.borrowersInQueue(),
        unendedCall: this.unendedCall,
        unknownCaller: this.unknownCaller(),
        allowCloseWrapUpDialog: (callWrapUpEvent.checkEndCall && this.sourceTabId === callWrapUpEvent.sourceTabId),
        callDuration: callWrapUpEvent.callDuration,
        callId: callWrapUpEvent.callId ?? null
      },
      panelClass: 'wrapUpDialogContainer',
      backdropClass: 'wrapUpBackdrop',
      position: { top: '150px' },
    });

    dialogRef.componentInstance.confirmedEndCall
      .pipe(takeUntil(this.destroyed$))
      .subscribe((updates: CallUpdates) => {
        this.endCall(updates);
        dialogRef.close();
        this.channel.postMessage({ action: 'confirmedEndCall', updates: updates, callId: this.activeCall?.callId ?? null });
      }
    );

    dialogRef.componentInstance.updateEndedCall
      .pipe(takeUntil(this.destroyed$))
      .subscribe((updates: CallUpdates) => {
        const dispositionedCallId = updates.dispositionId != null ? Number(updates.dispositionId) : null;

        const write$ = this.store.dispatch(new UpdateCall(updates));
        dialogRef.close();
        this.completeCallWrapUp();

        write$.pipe(take(1), takeUntil(this.destroyed$))
          .subscribe(() => {
            if (dispositionedCallId != null) {
              this.midCallCompletedForCallId = dispositionedCallId;
            }
            this.channel.postMessage({ action: 'updateEndedCall', updates: updates, callId: dispositionedCallId });
            this.store.dispatch(new LoadNeedsDisposition(true));
          });
      }
    );

    dialogRef.afterClosed().subscribe(() => {
      if (this.wrapUpDialogRef === dialogRef) {
        this.wrapUpDialogRef = undefined;
      }
    });
  }

  private isWrapUpDialogOpen(): boolean {
    return !!this.wrapUpDialogRef && this.wrapUpDialogRef.getState() === MatDialogState.OPEN;
  }

  private maybeForceDisposition(needsDisposition: NeedsDisposition | null): void {
    if (this.isWrapUpDialogOpen() || !needsDisposition?.hasOpen || !needsDisposition.callId) {
      return;
    }

    if (this.midCallCompletedForCallId === Number(needsDisposition.callId)) {
      return;
    }
    if (this.shouldUseForcedCxInteractionDrawer() || this.onActiveCall()) {
      return;
    }
    this.wrapUpDialog({ checkEndCall: false, sourceTabId: this.sourceTabId, callId: Number(needsDisposition.callId) });
  }

  private onActiveCall(): boolean {
    const callState = this.activeCall?.callState;
    return !!this.activeCall && callState !== 'disconnected' && callState !== 'canceled';
  }

  private shouldUseForcedCxInteractionDrawer(): boolean {
    const lenderUser = this.store.selectSnapshot(LenderUserState.lenderUser);

    return !!lenderUser?.team?.type?.includes('customer_service');
  }

  completeCallWrapUp() {
    //if we are on the today page, update today badge counts
    const borrowerId = this.activeCall?.borrowerId || this.dispositionCall?.borrowerId;
    if(this.router.url.includes('today')) {
      this.lenderUser$.pipe(take(1))
        .subscribe( lenderUser => {
          if(borrowerId && lenderUser) {
            this.store.dispatch( new CheckNewLeadCallTask(lenderUser.id, borrowerId) );
          }
        });
    }
    this.removeFromCallQueue();
    this.unendedCall = false;
    if (this.shouldClearActiveCall()) {
      this.timerOpen = false;
      localStorage.removeItem('currentCall');
      const shouldAdvance = this.shouldQueueAdvance();
      this.store.dispatch(new ClearDispositionCall());
      this.store.dispatch(new ClearActiveCall());
      if (shouldAdvance) {
        this.advanceQueue();
      }
    }
  }

  handleWrapUpBroadcast(data: WrapUpBroadcast): void {
    if (data.action === 'updateEndedCall') {
      this.dialog.closeAll();
      const dispositionedCallId = data.callId ?? data.updates?.dispositionId;
      if (dispositionedCallId != null) {
        this.midCallCompletedForCallId = Number(dispositionedCallId);
        this.store.dispatch(new MarkCallDispositioned(Number(dispositionedCallId)));
      }
      this.completeCallWrapUp();
      this.refreshNeedsDisposition();
    }
    if (data.action === 'confirmedEndCall') {
      this.dialog.closeAll();
      this.dropLocalCallIfMatches(data.callId ?? null);
      this.refreshNeedsDisposition();
    }
    if (data.action === 'forcedDispositionComplete') {
      this.completeCallWrapUp();
    }
  }

  private refreshNeedsDisposition(): void {
    this.store.dispatch(new LoadNeedsDisposition(true));
  }

  private dropLocalCallIfMatches(endedCallId: number | null): void {
    const localCallId = this.activeCall?.callId ?? this.dispositionCall?.callId ?? null;
    if (endedCallId == null || localCallId == null || endedCallId !== localCallId) {
      return;
    }
    this.store.dispatch(new ClearDispositionCall());
    this.store.dispatch(new ClearActiveCall());
  }

  borrowersInQueue() {
    if (!this.activeCall || !this.currentQueue?.length || !this.currentQueue[0]?.borrowerId) {
      return false;
    }

    return ((
      this.currentQueue.length > 0
      && this.currentQueue[0].borrowerId !== this.activeCall.borrowerId
    ) || (
      this.currentQueue.length > 1
      && this.currentQueue[0].borrowerId === this.activeCall.borrowerId
    ));
  }

  shouldClearActiveCall() {
    return (
      this.dispositionCall &&
      (!this.activeCall || this.dispositionCall.callId === this.activeCall.callId)
    ) || (
      this.activeCall && this.activeCall.callState === 'disconnected' &&
      !this.secondaryCall
    ) || (
      this.activeCall && /^(conference|internal|add) call/i.exec(<string> this.activeCall.disposition) &&
      !this.secondaryCall
    );
  }

  unknownCaller() {
    return (
      this.activeCall
      && (this.activeCall.borrowerId === -1 || !this.activeCall.borrowerId)
      && this.activeCall.businessName === 'Unknown Caller'
    );
  }

  removeFromCallQueue() {
    const call = this.dispositionCall?.callId ? this.dispositionCall
      : this.activeCall?.callId ? this.activeCall
      : null;
    if (!call?.borrowerId) {
      return;
    }

    if (
      this.currentQueue?.length > 0
      && this.currentQueue.some(queuedItem => queuedItem.borrowerId === call.borrowerId)
    ) {
      this.store.dispatch(new DeleteBorrowerFromUserQueue(call.borrowerId));
    }
  }

  dismissCallAlert(alertIndex: number) {
    this.store.dispatch(new DismissCallAlert(alertIndex));
  }

  handleMidCallDispositionCompleted(): void {
    this.midCallCompletedForCallId = this.activeCall?.callId ?? null;
  }

  handleActiveCallChange(call: ActiveCall | null): void {
    if (
      call?.callId
      && this.midCallCompletedForCallId !== null
      && this.midCallCompletedForCallId !== call.callId
    ) {
      this.midCallCompletedForCallId = null;
    }
  }
}
